/** ABGR packed the same way the original client stores colours. JSON may be negative; always `>>> 0` before masking. */

export function pack(r: number, g: number, b: number, a = 255): number {
  return (((a & 255) << 24) | ((b & 255) << 16) | ((g & 255) << 8) | (r & 255)) >>> 0;
}

export function hex(h: string, a = 255): number {
  const n = parseInt(h.replace("#", ""), 16);
  return pack((n >> 16) & 255, (n >> 8) & 255, n & 255, a);
}

export function u32(n: number): number {
  return n >>> 0;
}

export function rgba(n: number): string {
  n = n >>> 0;
  const r = n & 255;
  const g = (n >>> 8) & 255;
  const b = (n >>> 16) & 255;
  const a = ((n >>> 24) & 255) / 255;
  return `rgba(${r},${g},${b},${a.toFixed(3)})`;
}

export function rgb(n: number): string {
  n = n >>> 0;
  return `rgb(${n & 255},${(n >>> 8) & 255},${(n >>> 16) & 255})`;
}

export function mix(a: number, b: number, t: number): number {
  a = a >>> 0;
  b = b >>> 0;
  const u = 1 - t;
  return pack(
    (a & 255) * u + (b & 255) * t,
    ((a >>> 8) & 255) * u + ((b >>> 8) & 255) * t,
    ((a >>> 16) & 255) * u + ((b >>> 16) & 255) * t,
    ((a >>> 24) & 255) * u + ((b >>> 24) & 255) * t,
  );
}

export function lum(n: number): number {
  n = n >>> 0;
  return ((n & 255) * 0.3 + ((n >>> 8) & 255) * 0.59 + ((n >>> 16) & 255) * 0.11) / 255;
}

const cache = new Map<number, string>();

export function css(n: number): string {
  n = n >>> 0;
  let s = cache.get(n);
  if (!s) {
    s = rgba(n);
    if (cache.size > 4096) cache.clear();
    cache.set(n, s);
  }
  return s;
}

export function parseCssColor(input: string | null | undefined): number | null {
  if (!input) return null;
  let s = input.trim();
  if (s === "transparent" || s === "none") return null;
  let m = s.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+%?))?\s*\)$/i);
  if (m) {
    let a = 1;
    if (m[4] !== undefined) a = m[4].endsWith("%") ? parseFloat(m[4]) / 100 : parseFloat(m[4]);
    if (a <= 0.01) return null;
    return pack(+m[1], +m[2], +m[3], Math.round(a * 255));
  }
  m = s.match(/^#([0-9a-f]{3,8})$/i);
  if (m) {
    let h = m[1];
    if (h.length === 3 || h.length === 4) h = [...h].map((c) => c + c).join("");
    const rgb = parseInt(h.slice(0, 6), 16);
    const a = h.length === 8 ? parseInt(h.slice(6, 8), 16) : 255;
    if (a < 3) return null;
    return pack((rgb >> 16) & 255, (rgb >> 8) & 255, rgb & 255, a);
  }
  if (/^color\(srgb/.test(s)) {
    const nums = s.match(/[\d.]+/g)?.map(Number) ?? [];
    if (nums.length >= 3) return pack(nums[0] * 255, nums[1] * 255, nums[2] * 255, nums.length > 3 ? nums[3] * 255 : 255);
  }
  return null;
}

export function clamp(n: number, a: number, b: number): number {
  return Math.max(a, Math.min(b, n));
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function hash2(x: number, y: number): number {
  const n = Math.sin(x * 127.1 + y * 311.7) * 43758.5453;
  return n - Math.floor(n);
}

/** Ordered dither from the original sprite renderer. */
const BAYER = [0, 8, 2, 10, 12, 4, 14, 6, 3, 11, 1, 9, 15, 7, 13, 5];

export function bayer(x: number, y: number): number {
  return (BAYER[((y & 3) << 2) | (x & 3)] + 0.5) / 16;
}
